// Path: ./src/app/pages/CreateFlyerItemListPage/index.tsx
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  styled,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SearchInput } from "../../components/SearchInputField";
import { adminApiService } from "../../../utils/api";
import {
  GfFlyerItemList,
  MuiFilterRequestDto,
  AiGenerationStatus,
  FlyerItemListSearchTab,
} from "@swagger/typescript-fetch-goflyer";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { useFlyerItemListTableColumns } from "./useFlyerItemListTableColumns";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import {
  LeftButtonSection,
  RightSearchSection,
  TableWrapper,
  Wrapper,
} from "./styled";
import { CreateFlyerItemListModal } from "./CreateFlyerItemListModal";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useTableSettingsConfig } from "../../../hooks/useTableSettingsConfig";
import { SettingKey } from "@swagger/typescript-fetch-goflyer";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { AiErrorLogModal } from "./AiErrorLogModal";
import { routes } from "../../../utils/constant/navigationRoutes";
import { RetryCoordinatesConfirmDescription } from "../../components/RetryCoordinatesConfirmDescription";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";

const CustomToggleButton = styled(ToggleButton)({
  color: "#ff8038 !important",
  "&.Mui-selected": {
    backgroundColor: "#ff8038 !important",
    color: "white !important",
  },
});

type FlyerItemListTimeFrame = "validList" | "all" | "aiCoordinates";

const SEARCH_TAB_BY_TIME_FRAME: Record<
  FlyerItemListTimeFrame,
  FlyerItemListSearchTab
> = {
  validList: FlyerItemListSearchTab.Valid,
  aiCoordinates: FlyerItemListSearchTab.AI,
  all: FlyerItemListSearchTab.All,
};

const CreateFlyerItemListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();
  const { pageLimit } = useTableSettingsConfig(SettingKey.TableFlyerItemLists);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTimeFrame =
    (searchParams.get("timeFrame") as FlyerItemListTimeFrame) || "validList";

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams(pageLimit);

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [flyerListData, setFlyerListData] =
    useState<TableData<GfFlyerItemList>>();
  const [open, setOpen] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [copy, setCopy] = useState<boolean>(false);
  const [openErrorLogModal, setOpenErrorLogModal] = useState<boolean>(false);
  const [selectedItemList, setSelectedItemList] =
    useState<GfFlyerItemList | null>(null);
  const [retryingFlyerIds, setRetryingFlyerIds] = useState<string[]>([]);
  const [
    showRetryCoordinatesConfirmDialog,
    setShowRetryCoordinatesConfirmDialog,
  ] = useState(false);
  const [selectedRowForRetryCoordinates, setSelectedRowForRetryCoordinates] =
    useState<GfFlyerItemList | null>(null);

  const [updateFlyerItemList, setUpdateFlyerItemList] =
    useState<GfFlyerItemList>();

  /**
   * Opens the modal for editing a FlyerItemList.
   *
   * @function
   * @name handleEditFlyerItemList
   * @returns {void}
   */
  const handleEditFlyerItemList = (
    flyerListData: GfFlyerItemList,
    copy?: boolean,
  ) => {
    setOpenCreateModal(true);
    setUpdateFlyerItemList(flyerListData);
    copy && setCopy(copy);
  };

  /**
   * Opens the modal for viewing the AI Generation Error Log and run details.
   *
   * @function
   * @name handleErrorLogClick
   * @returns {void}
   */
  const handleErrorLogClick = (itemList: GfFlyerItemList) => {
    setSelectedItemList(itemList);
    setOpenErrorLogModal(true);
  };

  const updateFlyerItemListRow = (
    flyerItemListId: string,
    updater: (row: GfFlyerItemList) => GfFlyerItemList,
  ) => {
    setFlyerListData((prev) => ({
      ...prev,
      rows: prev?.rows?.map((row) =>
        row.id === flyerItemListId ? updater(row) : row,
      ),
    }));
  };

  const handleRetryCoordinates = (row: GfFlyerItemList) => {
    const flyerId = row.flyer?.id;
    if (!flyerId || !row.id) return;
    if (retryingFlyerIds.includes(flyerId)) return;

    setSelectedRowForRetryCoordinates(row);
    setShowRetryCoordinatesConfirmDialog(true);
  };

  const handleCancelRetryCoordinates = () => {
    setShowRetryCoordinatesConfirmDialog(false);
    setSelectedRowForRetryCoordinates(null);
  };

  const handleConfirmRetryCoordinates = async () => {
    const row = selectedRowForRetryCoordinates;
    const flyerId = row?.flyer?.id;
    const flyerItemListId = row?.id;
    if (!flyerId || !flyerItemListId) return;
    if (retryingFlyerIds.includes(flyerId)) return;

    setShowRetryCoordinatesConfirmDialog(false);
    setRetryingFlyerIds((prev) => [...prev, flyerId]);
    updateFlyerItemListRow(flyerItemListId, (currentRow) => ({
      ...currentRow,
      aiGenerationStatus: AiGenerationStatus.WAITING,
      aiGenerationErrorLog: undefined,
    }));

    try {
      await adminApiService.gfFlyerAdminControllerRetryFlyerCoordinates(
        flyerId,
        authHeaders,
      );

      const updatedRow =
        await adminApiService.getOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
          flyerItemListId,
          undefined,
          ["stores", "stores.merchant", "flyer"],
          undefined,
        );
      updateFlyerItemListRow(flyerItemListId, () => updatedRow);
      toastSuccess(t(messages.Retry_Coordinates()));
    } catch (error) {
      console.log("error", error);
      try {
        const updatedRow =
          await adminApiService.getOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
            flyerItemListId,
            undefined,
            ["stores", "stores.merchant", "flyer"],
            undefined,
          );
        updateFlyerItemListRow(flyerItemListId, () => updatedRow);
      } catch (refetchError) {
        console.log("refetch error", refetchError);
      }
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setRetryingFlyerIds((prev) => prev.filter((id) => id !== flyerId));
      setSelectedRowForRetryCoordinates(null);
    }
  };

  const flyerTableColumns = useFlyerItemListTableColumns(
    handleEditFlyerItemList,
    handleErrorLogClick,
    selectedTimeFrame === "aiCoordinates",
    handleRetryCoordinates,
    retryingFlyerIds,
  );

  /**
   * Opens a confirmation modal to delete a FlyerItemList.
   *
   * @function
   * @name handleOpenDelete
   * @returns {void}
   */
  const handleOpenDelete = () => {
    setOpen(true);
  };

  /**
   * Cancels the deletion operation and closes the confirmation modal.
   *
   * @function
   * @name handleCancel
   * @returns {void}
   */
  const handleCancel = () => {
    setOpen(false);
  };

  const fetchFlyerItemListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfFlyerItemListAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setFlyerListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  /**
   * Deletes selected flyer items list and updates the display accordingly.
   * If search text is provided, filters the displayed flyer items list.
   * Otherwise, fetches flyer items list based on pagination and sorting criteria.
   */
  const handleDeleteAndRefreshFlyerItemList = async () => {
    if (rowSelectionModel?.length) {
      try {
        setPaginationLoading(true);
        const Ids = rowSelectionModel?.join(",");
        await adminApiService.gfFlyerItemListAdminControllerBulkDelete(
          Ids,
          authHeaders,
        );

        if (searchText?.trim()?.length) {
          searchFlyerFn(searchText);
        } else {
          fetchFlyerItemList();
        }

        setPaginationLoading(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted_Flyers_Item_List()));
      } catch (error) {
        setPaginationLoading(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  const handleTimeFrameChange = (
    event: React.MouseEvent<HTMLElement>,
    newTimeFrame: string,
  ) => {
    if (paginationLoading) return;
    if (newTimeFrame !== null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev.toString());
        next.set("timeFrame", newTimeFrame);
        return next;
      });
    }
  };

  /**
   * Fetches the list of FlyerItemList items based on pagination and sorting parameters.
   *
   * @async
   * @function
   * @name fetchFlyerItemList
   * @returns {Promise<void>}
   */
  const fetchFlyerItemList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const conditions = [];

        const storesList =
          state.user?.parsedRoles?.name === SUPERADMIN
            ? []
            : state.user?.stores?.map((value) => {
                return `${value.id}`;
              });
        const today = new Date().toISOString();

        if (storesList && storesList.length) {
          conditions.push(`stores.id||$in||${storesList?.join(",")}`);
        }

        if (selectedTimeFrame === "validList") {
          conditions.push(`validEndDate||$gte||${today}`);
          conditions.push(`jobTestingRecord||$isnull`);
        } else if (selectedTimeFrame === "aiCoordinates") {
          conditions.push(`jobTestingRecord||$eq||AI_COORDINATE_GENERATION`);
        }

        return adminApiService.getManyBaseGfFlyerItemListAdminControllerGfFlyerItemList(
          undefined,
          undefined,
          conditions?.length ? conditions : undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          ["stores", "stores.merchant", "flyer"],
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        );
      },
      (flyerItemList) => {
        setFlyerListData((prev) => ({
          ...prev,
          rows: flyerItemList?.data,
          total: flyerItemList?.total,
        }));
      },
    );
  };

  /**
   * Handles the search operation for FlyerItemList items.
   *
   * @function
   * @name handleOnSearch
   * @param {string} value - The search value.
   * @returns {void}
   */
  const handleOnSearch = (value: string) => {
    setQueryParams({
      searchText: value,
      page: 1,
      sortField: null,
      sortOrder: null,
    });
  };

  const searchFlyerFn = async (searchValue: string) => {
    if (!searchValue?.length) return;

    const searchText = searchValue.toLowerCase().trim();

    const searchTab =
      SEARCH_TAB_BY_TIME_FRAME[selectedTimeFrame] ??
      SEARCH_TAB_BY_TIME_FRAME.validList;

    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.gfFlyerItemListAdminControllerSearch(
          searchText,
          paginationModel.page,
          paginationModel.pageSize,
          searchTab,
          {
            query: {
              join: ["stores", "stores.merchant", "flyer"],
            },
          },
        ),
      (response) => {
        setFlyerListData((prev) => ({
          ...prev,
          rows: response?.data,
          total: response?.total,
        }));
      },
    );
  };

  /**
   * Closes the CreateFlyerItemListModal and resets the updateFlyerItemList state.
   *
   * @function
   * @name onCloseModal
   * @returns {void}
   */
  const onCloseModal = () => {
    setOpenCreateModal(false);
    setUpdateFlyerItemList(undefined);
    setCopy(false);
  };

  /**
   * function called after saving FlyerItemList.
   * Closes the CreateFlyerItemListModal and fetches the updated list.
   *
   * @function
   * @name onSaveFlyerItemList
   * @returns {void}
   */
  const onSaveFlyerItemList = () => {
    setOpenCreateModal(false);
    setUpdateFlyerItemList(undefined);
    setCopy(false);
    fetchFlyerItemList();
  };

  const isShowingSearchResults =
    !muiFilterModelHasActiveItems(filterModel) && Boolean(searchText?.length);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchFlyerItemListWithFilter(filterModel);
    } else if (searchText?.length) {
      searchFlyerFn(searchText);
    } else {
      fetchFlyerItemList();
    }
  }, [selectedTimeFrame, paginationModel, sortModel, searchText, filterModel]);

  return (
    <Box data-testid="create-flyer-item-list-page">
      <SetupPageTopAppBar>
        <LeftButtonSection>
          <Stack
            direction={{ xs: "column", sm: "column", md: "row" }}
            spacing={{ xs: 0, sm: 0, md: 2 }}
            alignItems={{ xs: "flex-start", sm: "flex-start", md: "center" }}
            marginBottom={{ xs: "10px", sm: "10px", md: 0 }}
          >
            <LeftButtonSection>
              <BlackFontTransparentButton
                variant="text"
                startIcon={
                  <UploadFileIcon style={{ color: "#ff8038", fontSize: 25 }} />
                }
                onClick={() => {
                  setOpenCreateModal(true);
                }}
                data-testid="create-button"
              >
                {t(messages.Create())}
              </BlackFontTransparentButton>
              <PermissionComponentWrapper
                requiredPermission={["delete"]}
                requiredPermissionGroup={["flyer"]}
              >
                <BlackFontTransparentButton
                  variant="text"
                  startIcon={
                    <DeleteIcon
                      style={{
                        color: "#ff8038",
                        fontSize: 25,
                        opacity: !rowSelectionModel?.length ? 0.5 : 1,
                      }}
                    />
                  }
                  disabled={!rowSelectionModel?.length}
                  onClick={handleOpenDelete}
                >
                  {t(messages.Delete())}
                </BlackFontTransparentButton>
              </PermissionComponentWrapper>
            </LeftButtonSection>
            <ToggleButtonGroup
              value={selectedTimeFrame}
              exclusive
              onChange={handleTimeFrameChange}
              aria-label="Platform"
              size="small"
              sx={{ height: "30px" }}
              disabled={paginationLoading}
            >
              <CustomToggleButton
                value="validList"
                sx={{
                  gap: "6px",
                  paddingRight: "10px",
                  textTransform: "capitalize",
                  lineHeight: "10px",
                }}
              >
                <Tooltip title={t(messages.DISPLAYS_VALID_ITEMS())}>
                  <Wrapper>
                    <DateRangeIcon
                      style={{
                        fontSize: 20,
                      }}
                    />
                    {t(messages.VALID_LIST())}
                  </Wrapper>
                </Tooltip>
              </CustomToggleButton>
              <CustomToggleButton
                value="all"
                sx={{
                  gap: "6px",
                  textTransform: "capitalize",
                  paddingRight: "20px",
                }}
              >
                <Tooltip
                  title={t(
                    messages.DISPLAYS_BOTH_EXPIRED_AND_NON_EXPIRED_ITEMS(),
                  )}
                >
                  <Wrapper>
                    <AllInclusiveIcon
                      style={{
                        fontSize: 20,
                      }}
                    />
                    {t(messages.All())}{" "}
                  </Wrapper>
                </Tooltip>
              </CustomToggleButton>
              <CustomToggleButton
                value="aiCoordinates"
                sx={{
                  gap: "6px",
                  textTransform: "capitalize",
                  paddingRight: "20px",
                }}
              >
                <Tooltip title="Displays items with AI Coordinate Generation">
                  <Wrapper>
                    <AutoFixHighIcon
                      style={{
                        fontSize: 20,
                      }}
                    />
                    AI
                  </Wrapper>
                </Tooltip>
              </CustomToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </LeftButtonSection>
        {(state.user?.parsedRoles?.name === SUPERADMIN ||
          !state.user?.stores?.length) && (
          <RightSearchSection>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ width: "100%" }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <SearchInput
                  onSearch={handleOnSearch}
                  initialValue={searchText}
                ></SearchInput>
              </Box>
              <Tooltip
                title={t(messages.Search_Tab_Dependent_Info())}
                placement="top"
                arrow
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 18,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            </Stack>
          </RightSearchSection>
        )}
      </SetupPageTopAppBar>
      <TableWrapper>
        <ListTableQueryPagination
          loadFailed={listRequestGuard.loadFailed}
          sortingMode={isShowingSearchResults ? "client" : "server"}
          columns={flyerTableColumns}
          rows={flyerListData?.rows}
          total={paginationLoading ? 0 : flyerListData?.total}
          paginationModel={paginationModel}
          sortModel={sortModel}
          isLoading={paginationLoading}
          onTableChange={handleTableChange}
          handleRowSelection={(value) => {
            setRowSelectionModel(value);
          }}
          onRowClick={(row) => {
            navigate(routes.createFlyer.byId(String(row?.id)));
          }}
          dynamicHeightAdjuster={210}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
        />
      </TableWrapper>

      {openCreateModal ? (
        <CreateFlyerItemListModal
          onCloseModal={onCloseModal}
          onSaveFlyerItemList={onSaveFlyerItemList}
          updateFlyerItemList={updateFlyerItemList}
          copy={copy}
        />
      ) : (
        <></>
      )}
      <AiErrorLogModal
        open={openErrorLogModal}
        onClose={() => {
          setOpenErrorLogModal(false);
          setSelectedItemList(null);
        }}
        errorLog={selectedItemList?.aiGenerationErrorLog ?? null}
        runDetails={selectedItemList}
      />
      <MuiConfirmationModal
        open={showRetryCoordinatesConfirmDialog}
        handleCancel={handleCancelRetryCoordinates}
        handleConfirm={handleConfirmRetryCoordinates}
        title={`${t(messages.Retry_Coordinates_Title())}`}
        description={
          selectedRowForRetryCoordinates ? (
            <RetryCoordinatesConfirmDescription
              storeName={
                selectedRowForRetryCoordinates.stores?.[0]
                  ? makeStoreDisplayName(
                      selectedRowForRetryCoordinates.stores[0],
                    )
                  : undefined
              }
              validStartDate={selectedRowForRetryCoordinates.validStartDate}
              validEndDate={selectedRowForRetryCoordinates.validEndDate}
              imageCount={
                selectedRowForRetryCoordinates.flyer?.sourceImageCount ??
                selectedRowForRetryCoordinates.aiGenerationErrorLog?.totalImages
              }
            />
          ) : (
            t(messages.Retry_Coordinates_Description())
          )
        }
      />
      <MuiConfirmationModal
        open={open}
        handleCancel={handleCancel}
        handleConfirm={handleDeleteAndRefreshFlyerItemList}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Flyers_Item_Lists(),
          {
            count: rowSelectionModel.length,
          },
        )}. ${t(
          messages.FlyerItems_related_to_this_FlyerItemList_will_also_be_deleted(),
        )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
      />
    </Box>
  );
};

export default CreateFlyerItemListPage;
